import React from 'react';
import {
  C,
  CARD_W,
  imgUrl,
  normalizeImgField,
} from '../flowCanvas.constants';
import { IMAGE_OVERLAY_CARD_H } from './ImageOverlayCardNode';

const BADGE_W = 40;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function hexToHSL(hex) {
  const normalized = String(hex || '#FFFFFF').replace('#', '').trim();
  const safe = normalized.length === 3
    ? normalized.split('').map(ch => `${ch}${ch}`).join('')
    : normalized.padEnd(6, 'F').slice(0, 6);

  const r = parseInt(safe.slice(0, 2), 16) / 255;
  const g = parseInt(safe.slice(2, 4), 16) / 255;
  const b = parseInt(safe.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function getContrastTextColor(hex) {
  const normalized = String(hex || '#FFFFFF').replace('#', '').trim();
  const safe = normalized.length === 3
    ? normalized.split('').map(ch => `${ch}${ch}`).join('')
    : normalized.padEnd(6, 'F').slice(0, 6);

  const r = parseInt(safe.slice(0, 2), 16);
  const g = parseInt(safe.slice(2, 4), 16);
  const b = parseInt(safe.slice(4, 6), 16);
  const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
  return luminance > 160 ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.92)';
}

function getFireworksVarianceRange(item, colourValue) {
  const hsl = hexToHSL(colourValue);
  const hueVariance = Number(item?.hueVariance) || 0;
  const saturationVariance = Number(item?.saturationVariance) || 0;
  const brightnessVariance = Number(item?.brightnessVariance) || 0;

  let minHue = hsl.h - Math.floor(hueVariance / 2);
  if (minHue < 0) minHue = 0;
  let maxHue = minHue + hueVariance;
  if (maxHue > 360) {
    maxHue = 360;
    minHue = maxHue - hueVariance;
  }

  let minBrightness = hsl.l - Math.floor(brightnessVariance);
  if (minBrightness < 0) minBrightness = 0;
  let maxBrightness = minBrightness + brightnessVariance;
  if (maxBrightness > 100) {
    maxBrightness = 100;
    minBrightness = maxBrightness - brightnessVariance;
  }

  let minSaturation = hsl.s - Math.floor(saturationVariance / 2);
  if (minSaturation < 0) minSaturation = 0;
  let maxSaturation = minSaturation + saturationVariance;
  if (maxSaturation > 100) {
    maxSaturation = 100;
    minSaturation = maxSaturation - saturationVariance;
  }

  return {
    base: hsl,
    minHue: clamp(minHue, 0, 360),
    maxHue: clamp(maxHue, 0, 360),
    minSaturation: clamp(minSaturation, 0, 100),
    maxSaturation: clamp(maxSaturation, 0, 100),
    minBrightness: clamp(minBrightness, 0, 100),
    maxBrightness: clamp(maxBrightness, 0, 100),
    hasVariance: hueVariance > 0 || saturationVariance > 0 || brightnessVariance > 0,
  };
}

function sampleVarianceStops(range, samples = 9) {
  return Array.from({ length: samples }, (_, index) => {
    const t = samples === 1 ? 0 : index / (samples - 1);
    const hue = Math.round(range.minHue + (range.maxHue - range.minHue) * t);
    const saturation = Math.round(range.minSaturation + (range.maxSaturation - range.minSaturation) * t);
    const brightness = Math.round(range.minBrightness + (range.maxBrightness - range.minBrightness) * t);
    return {
      offset: `${Math.round(t * 100)}%`,
      colour: `hsl(${clamp(hue, 0, 360)} ${clamp(saturation, 0, 100)}% ${clamp(brightness, 0, 100)}%)`,
    };
  });
}

export default function ParticleStackCardNode({
  item,
  index,
  selected,
  onClick,
  onDelete,
  itemCount = 0,
  configuredItemCount = 0,
  overrideX,
  overrideY,
  assetFolder,
  campaignId,
  variant = 'image',
  title,
  subtitle,
  frequencyPct = 0,
  showFireworksVariancePreview = false,
}) {
  const W = CARD_W;
  const H = IMAGE_OVERLAY_CARD_H;
  const x = overrideX;
  const y = overrideY;
  const left = x - W / 2;
  const top = y - H / 2;
  const previewX = left + 8;
  const previewY = top + 44;
  const previewW = W - 16;
  const previewH = 92;
  const varianceStripY = previewY + previewH + 16;
  const varianceStripH = 10;

  const imageField = variant === 'image' ? item?.image : null;
  const imgNorm = normalizeImgField(imageField);
  const imgSrc = imgNorm.srcUrl
    ? imgNorm.srcUrl
    : imgNorm.name ? imgUrl(imgNorm.name, imgNorm.isDefault, assetFolder, campaignId) : null;

  const colourValue = typeof item?.colour === 'string' ? item.colour : '#FFFFFF';
  const shortTitle = (title || `${variant === 'colour' ? 'Colour' : 'Custom Image'} ${index + 1}`).slice(0, 28);
  const footerValue = variant === 'colour'
    ? colourValue.toUpperCase()
    : (imgNorm.name || 'No image set');
  const shortFooter = footerValue.length > 28 ? `${footerValue.slice(0, 26)}…` : footerValue;
  const isConfigured = variant === 'colour' ? true : !!imgNorm.name;
  const clipId = `fc-particle-stack-${variant}-${Math.round(x)}-${Math.round(y)}-${index}`;
  const colourTextFill = getContrastTextColor(colourValue);
  const varianceRange = variant === 'colour' && showFireworksVariancePreview
    ? getFireworksVarianceRange(item, colourValue)
    : null;
  const varianceStops = varianceRange ? sampleVarianceStops(varianceRange) : [];
  const varianceGradientId = `fc-particle-variance-${Math.round(x)}-${Math.round(y)}-${index}`;
  const canShowDelete = Boolean(onDelete) && itemCount > 1 && configuredItemCount > 1;

  return (
    <g className="fc-node fc-node--particle-stack-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x={previewX} y={previewY} width={previewW} height={previewH} rx="6" />
        </clipPath>
        {varianceStops.length > 0 && (
          <linearGradient id={varianceGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            {varianceStops.map(stop => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.colour} />
            ))}
          </linearGradient>
        )}
      </defs>

      <rect x={left + 2} y={top + 4} width={W} height={H} rx="8" fill="#000" fillOpacity="0.3" />
      <rect
        x={left}
        y={top}
        width={W}
        height={H}
        rx="8"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : (isConfigured ? C.purple : C.orange)}
        strokeWidth={selected ? 2 : 1.5}
      />

      <text
        x={left + 12}
        y={top + 18}
        fontSize="11"
        fontWeight="700"
        fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {shortTitle}
      </text>
      <text
        x={left + 12}
        y={top + 31}
        fontSize="9.5"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {subtitle}
      </text>

      <rect
        x={previewX}
        y={previewY}
        width={previewW}
        height={previewH}
        rx="6"
        fill={variant === 'colour' ? colourValue : 'rgba(255,255,255,0.04)'}
        stroke={variant === 'colour' ? 'rgba(255,255,255,0.18)' : 'rgba(145,70,255,0.18)'}
        strokeWidth="1"
      />

      {variant === 'image' && imgSrc && (
        <image
          href={imgSrc}
          x={previewX}
          y={previewY}
          width={previewW}
          height={previewH}
          preserveAspectRatio="xMidYMid meet"
          clipPath={`url(#${clipId})`}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {variant === 'image' && !imgSrc && (
        <>
          <text
            x={x}
            y={previewY + previewH / 2 - 6}
            textAnchor="middle"
            fontSize="26"
            fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            ⊕
          </text>
          <text
            x={x}
            y={previewY + previewH / 2 + 16}
            textAnchor="middle"
            fontSize="9"
            fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            Click to set image
          </text>
        </>
      )}

      {variant === 'colour' && (
        <text
          x={x}
          y={previewY + previewH / 2 - (varianceStops.length > 0 ? 6 : 0)}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill={colourTextFill}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {colourValue.toUpperCase()}
        </text>
      )}

      {variant === 'colour' && showFireworksVariancePreview && varianceStops.length > 0 && (
        <g style={{ pointerEvents: 'none', userSelect: 'none' }}>
          <rect
            x={previewX + 8}
            y={varianceStripY}
            width={previewW - 16}
            height={varianceStripH}
            rx="5"
            fill={`url(#${varianceGradientId})`}
            stroke="rgba(255,255,255,0.55)"
            strokeWidth="0.8"
          />
          <text
            x={x}
            y={varianceStripY - 4}
            textAnchor="middle"
            fontSize="7.5"
            fontWeight="700"
            fill="rgba(255,255,255,0.7)"
          >
            COLOUR VARIATION RANGE
          </text>
        </g>
      )}

      <g style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <rect
          x={x - BADGE_W / 2}
          y={previewY + previewH - 15}
          width={BADGE_W}
          height={13}
          rx="6"
          fill="#000"
          fillOpacity="0.65"
          clipPath={`url(#${clipId})`}
        />
        <text
          x={x}
          y={previewY + previewH - 8}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="8"
          fontWeight="700"
          fill="#86efac"
        >
          {frequencyPct}%
        </text>
      </g>

      {variant !== 'colour' && (
        <text
          x={x}
          y={top + H - 14}
          textAnchor="middle"
          fontSize="8.5"
          fill={C.textMuted}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {shortFooter}
        </text>
      )}

      {canShowDelete && (
        <g onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ cursor: 'pointer' }}>
          <circle cx={left + W - 12} cy={top + 12} r={10} fill="#dc2626" />
          <g transform={`translate(${left + W - 12}, ${top + 12})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
            <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
            <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
            <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
          </g>
        </g>
      )}
    </g>
  );
}
